import { DocumentNode, gql } from '@apollo/client';
import { Center, HStack, StackProps } from '@chakra-ui/react';
import * as React from 'react';

import { LogInSignUpModal } from '@/components/LogInSignUp';
import { PaywallModal } from '@/components/Paywall';
import { useShowPostResponses } from '@/hooks/useShowPostResponses';

import { LikeCommentFooter_PostFragment } from './graphql-types/LikeCommentFooter_PostFragment';

import { useSponsorPaywallModal } from '../../hooks/useSponsorPaywallModal';
import { CommentButton } from './components/CommentButton';
import { CommentCount } from './components/CommentCount';
import { PostLikeButton } from './components/LikeButton/PostLikeButton';
import { LikeCount } from './components/LikeCount/LikeCount';

export const LikeCommentFooter: React.FC<
  {
    post: LikeCommentFooter_PostFragment;
    onClickComment?: () => void;
    onClickCommentCount?: () => void;
  } & StackProps
> & { postFragment: DocumentNode } = ({
  post,
  onClickComment,
  onClickCommentCount,
  ...stackProps
}) => {
  const sponsorPaywallModalProps = useSponsorPaywallModal(post);

  const {
    showPostResponses,
    behaviorOverride,
    paywallDisclosure,
    loginDisclosure,
  } = useShowPostResponses(post);

  return (
    <>
      <HStack
        py="1"
        borderTopWidth={1}
        borderTopColor="gray.100"
        borderBottomWidth={1}
        borderBottomColor="gray.100"
        justifyContent="space-between"
        alignItems="center"
        width="full"
        {...stackProps}
      >
        <Center>
          {post.__typename !== 'AnswerV2' && <LikeCount post={post} />}
          <CommentCount
            post={post}
            onClick={behaviorOverride ? behaviorOverride : onClickCommentCount}
          />
        </Center>
        {!!showPostResponses && (
          <Center>
            <CommentButton
              onClick={behaviorOverride ? behaviorOverride : onClickComment}
            />
            {post.__typename !== 'AnswerV2' && (
              <PostLikeButton post={post} onClickOverride={behaviorOverride} />
            )}
          </Center>
        )}
      </HStack>
      <PaywallModal
        isOpen={paywallDisclosure.isOpen}
        onClose={paywallDisclosure.onClose}
        source="respond to post paywall"
        {...sponsorPaywallModalProps}
      />
      <LogInSignUpModal
        isOpen={loginDisclosure.isOpen}
        onClose={loginDisclosure.onClose}
      />
    </>
  );
};

LikeCommentFooter.postFragment = gql`
  fragment LikeCommentFooter_PostFragment on PostV2 {
    _id
    ...PostLikeButton_PostFragment
    ...LikeCount_PostFragment
    ...CommentCount_PostFragment
    ...useShowPostResponses_PostFragment
    ...useSponsorPaywallModal_PostFragment
  }
  ${PostLikeButton.postFragment}
  ${LikeCount.postFragment}
  ${CommentCount.postFragment}
  ${useShowPostResponses.postFragment}
  ${useSponsorPaywallModal.postFragment}
`;
